import { Field } from 'formik';
import type { FormikHelpers } from 'formik';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import FormArticleInput from 'components/forms/FormArticleInput';
import InputPriceRate from 'components/others/InputPriceRate';
import DimensionSelect from '../../DimensionSelect';
import VendorSelect from '../../VendorSelect';
import type { ProductEditType } from '../../types';
import type { SelectOption } from '../../types';

interface ProductFormGeneralProps {
    isSubmitting: boolean;
    setFieldValue: FormikHelpers<ProductEditType>['setFieldValue'];
    values: ProductEditType;
    product: ProductEditType;
}

const ProductFormGeneral = ({ setFieldValue, values, product }: ProductFormGeneralProps) => {
    const { auth } = useAuth();
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const { t } = useTranslation(['Products', 'common']);
    const isCreate = Number(product?.ID || values?.ID || 0) === 0;

    return (
        <>
            <FormArticleInput
                label={t('fields.article')}
                name="prodArticle"
                disabled={!isCreate || !canEdit}
                canGenerate={isCreate && canEdit}
                onChange={(article) => setFieldValue('prodArticle', article)}
            />

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.name')}</label>
                <Field name="prodName" disabled={!canEdit} className="form-control" />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.price')}</label>
                <InputPriceRate
                    value={values.prodDefPrice || 0}
                    onValueChange={(val) => {
                        setFieldValue('prodDefPrice', val.floatValue);
                    }} 
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.priceSale')}</label>
                <InputPriceRate
                    value={values.prodDefPriceSale || 0}
                    onValueChange={(val) => {
                        setFieldValue('prodDefPriceSale', val.floatValue);
                    }}
                /> 
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.vendor')}</label>
                <VendorSelect
                    required={false}
                    value={values.vendorId}
                    onChange={(opt: SelectOption | null) => {
                        setFieldValue('vendorId', opt?.value);
                    }}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.dimension')}</label>
                <DimensionSelect
                    required={false}
                    value={values.dimensionId}
                    onChange={(opt: SelectOption | null) => {
                        setFieldValue('dimensionId', opt?.value);
                    }}
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('fields.description')}</label>
                <Field
                    name="prodDescription"
                    as="textarea"
                    rows="4"
                    disabled={!canEdit}
                    className="form-control"
                />
            </Form.Group>
        </>
    );
};

export default ProductFormGeneral;
